"use client";

import React, { useEffect, useRef, useState } from "react";
import { Network, Server } from "lucide-react";
import { Separator } from "@/components/ui/separator";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function AnimatedNumber({
  value,
  suffix = "",
  prefix = "",
  decimals = 0
}: {
  value: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLSpanElement>({
    threshold: 0.5
  });
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    const duration = 2000;
    const startTime = performance.now();
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCurrent(value * easeProgress);
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCurrent(value);
      }
    };
    requestAnimationFrame(animate);
  }, [isVisible, value]);
  return <span ref={ref} className="tabular-nums">
      {prefix}
      {current.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    })}
      {suffix}
    </span>;
}
const principles = [{
  title: "Investment Thesis",
  description: "The AI infrastructure market is accelerating from $110B to $1.3T by 2032, driven by surging demand for GPU-ready, high-density, energy-integrated data centers. N2N Capital is positioned at this inflection point — deploying capital in power-constrained, AI-undersupplied markets where execution expertise creates durable competitive moats.\n\nAI workloads are increasing power density requirements by up to 10× versus traditional cloud — creating a structural repricing of infrastructure assets and a generational investment opportunity for disciplined, operator-led platforms.",
  icon: Network,
  metricLabel: "Efficiency Ratio",
  metricValue: 14.5,
  metricSuffix: "x",
  decimals: 1
}, {
  title: "A Mudrasys Company",
  description: "N2N Capital operates under the Mudrasys family of companies, combining deep technology investment expertise with operational infrastructure execution across the United States, Asia, and the Middle East.\n\nHeadquarters: 228 Hamilton Ave, 3rd Floor, Palo Alto, CA 94301 · +1 925-694-8999",
  icon: Server,
  metricLabel: "Uptime Target",
  metricValue: 99.999,
  metricSuffix: "%",
  decimals: 3
}];
function PrincipleRow({
  principle,
  index
}: {
  principle: (typeof principles)[0];
  index: number;
}) {
  const {
    ref
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const Icon = principle.icon;
  return <div ref={ref} data-index={index} className="relative flex flex-col gap-8 ![padding:50px_0px_1px_0px] !justify-start md:flex-row">
      <Separator className="absolute top-0 left-0 w-full bg-[#1C1917]/20" />

      <div className="w-16 shrink-0 pt-2">
        <div className="w-16 h-16 rounded-2xl bg-[#F5EDD8] border border-[#D8C29A] flex items-center justify-center shadow-sm">
          <Icon strokeWidth={1.7} className="w-8 h-8 text-[#B45309]" />
        </div>
      </div>

      <div className="flex-1 flex flex-col justify-center">
        <h3 className="font-sans text-[32px] leading-tight font-medium text-[#1C1917] tracking-tight mb-4">
          {principle.title}
        </h3>

        <p className="font-serif text-[#1C1917]/65 text-lg leading-relaxed mb-8 max-w-3xl whitespace-pre-line">
          {principle.description}
        </p>
      </div>
    </div>;
}
export default function InvestmentPhilosophy() {
  return <section className="py-24 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="
            relative bg-[#FDFAF3] rounded-2xl p-8 md:p-16 lg:p-20 shadow-xl overflow-hidden
            before:content-[''] before:absolute before:top-0 before:left-0
            before:w-[560px] before:h-[560px] before:bg-[#E7D8B8]/50
            before:blur-[95px] before:rounded-full
            before:-translate-x-1/3 before:-translate-y-1/3
            before:pointer-events-none
          ">
          <div className="relative z-10 max-w-4xl">
            <div className="inline-block px-4 py-1.5 rounded-full border border-[#B45309] mb-10">
              <span className="text-[10px] md:text-xs font-semibold tracking-[0.2em] text-[#B45309] uppercase">
                Institutional AI Infrastructure Capital
              </span>
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-[#1C1917] leading-[1.1] tracking-tight mb-8">
              Deploying Capital Into the{" "}
              <span className="text-[#B45309]">Backbone</span> of Artificial
              Intelligence
            </h1>

            <p className="text-lg md:text-xl text-[#1C1917]/70 leading-relaxed mb-12 max-w-2xl">
              N2N Capital is an infrastructure investment firm building a
              phased 1GW AI-ready data center platform across the United States,
              India, and Mexico.
            </p>

            <div className="flex flex-wrap items-center gap-x-8 gap-y-4 mb-20">
              <div className="flex flex-wrap items-center gap-6">
                <span className="text-xs md:text-sm font-bold text-[#B45309] tracking-widest uppercase">
                  1GW Pipeline
                </span>
                <div className="h-4 w-px bg-[#B45309]/30" />
                <span className="text-xs md:text-sm font-bold text-[#B45309] tracking-widest uppercase">
                  120 MW Under Site
                </span>
                <div className="h-4 w-px bg-[#B45309]/30" />
                <span className="text-xs md:text-sm font-bold text-[#B45309] tracking-widest uppercase">
                  +55 Yrs Experience
                </span>
              </div>
            </div>
          </div>

          <div className="relative z-10 grid grid-cols-1 gap-12 mt-12">
            {principles.map((principle, index) => <PrincipleRow key={index} principle={principle} index={index} />)}
          </div>
        </div>

        
      </div>
    </section>;
}